import { useQuery } from '@apollo/client';
import type { LabelType } from 'types/graphql';
import LabelsApi from './Labels.api';
import { type LabelAttachmentTarget, useLabelAttachmentsStore } from './labelAttachments.store';
import { type LabelDef, useLabelDefinitionsStore } from './labelDefinitions.store';

type PageEdges = ReadonlyArray<{ node?: LabelDef | null } | null> | null | undefined;

export const labelDefinitionNodes = (edges: PageEdges): LabelDef[] =>
  (edges ?? []).flatMap((edge) => (edge?.node ? [edge.node] : []));

/**
 * Seeds the label stores (P7-R35, FU-LBLDEF):
 *
 *   LabelAttachmentsQuery ─► attachments store + the attached labels' definitions (no extra read)
 *   LabelDefinitionsQuery ─► the first page of the store's types, by name
 *
 * Definitions past the first page arrive through the add-label picker's server search.
 */
export default function useLabelStoreHydration(attachedTo?: LabelAttachmentTarget) {
  const types = useLabelDefinitionsStore((state) => state.labelTypes);

  const upsertAvailableLabels = useLabelDefinitionsStore((state) => state?.upsertAvailableLabels);
  const setMoreLabelsOnServer = useLabelDefinitionsStore((state) => state?.setMoreLabelsOnServer);
  const setAttachedLabels = useLabelAttachmentsStore((state) => state.setAttachedLabels);

  useQuery(LabelsApi.GetAttachedLabels, {
    variables: { attachedTo },
    skip: !attachedTo,
    onCompleted: (data) => {
      setAttachedLabels(data.labelAttachments);
      // Only the definitions the store's types list, as the seed and the search filter them.
      upsertAvailableLabels(
        data.labelAttachments.flatMap(({ labelDefinition }: { labelDefinition: LabelDef | null }) =>
          labelDefinition?.labelTypes?.some((type: LabelType) => types.includes(type)) ? [labelDefinition] : []
        )
      );
    },
  });

  useQuery(LabelsApi.GetDefinitions, {
    variables: { types },
    onCompleted: (data) => {
      const seed = labelDefinitionNodes(data.labelDefinitionPage.edges);
      upsertAvailableLabels(seed);
      setMoreLabelsOnServer((data.labelDefinitionPage.totalCount ?? 0) > seed.length);
    },
  });
}
